import React, { useCallback } from 'react';
import { toast } from 'react-toastify';
import ToggleSwitch from 'components/ToggleSwitch';
import {
  useGetPrivacyPreferencesQuery,
  useUpdatePrivacyPreferencesMutation,
  PrivacyPreferences,
} from 'store/services/settings/settingsApi';
import './PrivacyTab.css';

type PrivacyKey =
  | 'allowFollowers'
  | 'showWantActivityToFollowers'
  | 'showTradeActivityToFollowers';

const PrivacyTab: React.FC = () => {
  const { data, isLoading } = useGetPrivacyPreferencesQuery();
  const [updatePreferences, { isLoading: isUpdating }] =
    useUpdatePrivacyPreferencesMutation();

  const handleToggle = useCallback(
    async (key: PrivacyKey, value: boolean) => {
      try {
        await updatePreferences({ [key]: value } as Partial<PrivacyPreferences>).unwrap();
      } catch {
        toast.error('Failed to update privacy setting. Please try again.');
      }
    },
    [updatePreferences],
  );

  const disabled = isLoading || isUpdating;

  return (
    <div className="privacy-tab">
      <h3 className="settings-section-title">Privacy</h3>
      <p className="privacy-tab__intro">
        Control who can follow you and what activity they see in their newsfeed.
      </p>

      <div className="notification-setting-item">
        <div className="notification-setting-label">Allow others to follow me</div>
        <div className="notification-setting-description">
          When off, existing followers are unaffected but new follows are blocked.
        </div>
        <div className="notification-setting-toggle">
          <ToggleSwitch
            checked={data?.allowFollowers ?? true}
            onChange={(v) => handleToggle('allowFollowers', v)}
            disabled={disabled}
          />
        </div>
      </div>

      <div className="notification-setting-item">
        <div className="notification-setting-label">
          Show my want list additions to followers
        </div>
        <div className="notification-setting-description">
          Followers can see when you add cards to your want list.
        </div>
        <div className="notification-setting-toggle">
          <ToggleSwitch
            checked={data?.showWantActivityToFollowers ?? true}
            onChange={(v) => handleToggle('showWantActivityToFollowers', v)}
            disabled={disabled}
          />
        </div>
      </div>

      <div className="notification-setting-item">
        <div className="notification-setting-label">
          Show my trade completions to followers
        </div>
        <div className="notification-setting-description">
          Followers can see when you complete a trade.
        </div>
        <div className="notification-setting-toggle">
          <ToggleSwitch
            checked={data?.showTradeActivityToFollowers ?? true}
            onChange={(v) => handleToggle('showTradeActivityToFollowers', v)}
            disabled={disabled}
          />
        </div>
      </div>
    </div>
  );
};

export default PrivacyTab;
